import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, Pencil, Check, X, Layers } from 'lucide-react';
import { toast } from 'sonner';
import useEquipmentCatalog from '../hooks/useEquipmentCatalog';
import { CatalogCategoryControls } from './CatalogCategoryControls';
import { CatalogLoadState } from './CatalogLoadState';
import { extractErrorMessage } from '../utils/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Column({ title, items, selected, onSelect, onAdd, onRename, onDelete, disabled, busy, placeholder, testPrefix }) {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState('');

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    if (await onAdd(draft.trim())) setDraft('');
  };

  return (
    <div className="flex flex-col rounded-md border border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 min-h-[280px]" data-testid={`${testPrefix}-column`}>
      <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
        <span className="text-xs font-bold uppercase text-slate-500 dark:text-slate-300" data-testid={`${testPrefix}-title`}>{title}</span>
        <span className="text-xs text-slate-400" data-testid={`${testPrefix}-count`}>{items.length}</span>
      </div>
      <ul className="flex-1 overflow-y-auto max-h-72 divide-y divide-slate-100">
        {disabled && <li className="px-3 py-6 text-center text-xs text-slate-400">Select a {title === 'Models' ? 'manufacturer' : title === 'Styles' ? 'category' : 'style'} first</li>}
        {!disabled && items.length === 0 && <li className="px-3 py-6 text-center text-xs text-slate-400">Nothing yet — add one below</li>}
        {!disabled && items.map((item) => (
          <li key={item} className={`group flex items-center gap-1 px-2 py-1.5 text-sm cursor-pointer ${selected === item ? 'bg-slate-900 text-white dark:bg-slate-700' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
            onClick={() => !busy && onSelect && onSelect(item)} data-testid={`${testPrefix}-item-${encodeURIComponent(item)}`}>
            {editing === item ? (
              <form className="flex flex-1 min-w-0 items-center gap-1" onSubmit={async (e) => { e.preventDefault(); if (await onRename(item, editVal.trim())) setEditing(null); }} onClick={(e) => e.stopPropagation()} data-testid={`${testPrefix}-rename-form`}>
                <Input autoFocus value={editVal} disabled={busy} onChange={(e) => setEditVal(e.target.value)} className="h-7 text-sm text-slate-900 bg-white dark:bg-slate-800 dark:text-slate-100" data-testid={`${testPrefix}-rename-input`} />
                <button type="submit" disabled={busy || !editVal.trim()} aria-label="Save name" title="Save name" className="text-emerald-600" data-testid={`${testPrefix}-rename-save`}><Check size={14} /></button>
                <button type="button" disabled={busy} aria-label="Cancel rename" title="Cancel rename" onClick={() => setEditing(null)} className="text-slate-400" data-testid={`${testPrefix}-rename-cancel`}><X size={14} /></button>
              </form>
            ) : (
              <>
                <span className="flex-1 min-w-0 truncate">{item}</span>
                <button type="button" disabled={busy} className={`shrink-0 opacity-70 hover:opacity-100 ${selected === item ? 'text-slate-300 hover:text-white' : 'text-slate-400 hover:text-slate-900'}`}
                  onClick={(e) => { e.stopPropagation(); setEditing(item); setEditVal(item); }} title={`Rename ${item}`} aria-label={`Rename ${item}`} data-testid={`${testPrefix}-rename-${encodeURIComponent(item)}`}>
                  <Pencil size={13} />
                </button>
                <button type="button" disabled={busy} className={`shrink-0 opacity-70 hover:opacity-100 ${selected === item ? 'text-slate-300 hover:text-white' : 'text-slate-400 hover:text-red-600'}`}
                  onClick={(e) => { e.stopPropagation(); onDelete(item); }} title={`Delete ${item}`} aria-label={`Delete ${item}`} data-testid={`${testPrefix}-delete-${encodeURIComponent(item)}`}>
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      {!disabled && (
        <form onSubmit={submitAdd} className="flex gap-1 p-2 border-t border-slate-200" data-testid={`${testPrefix}-add-form`}>
          <Input value={draft} disabled={busy} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} className="h-8 min-w-0 text-sm bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100" data-testid={`${testPrefix}-add-input`} />
          <Button type="submit" size="sm" className="h-8 px-2 bg-slate-900" aria-label={placeholder} title={placeholder} disabled={busy || !draft.trim()} data-testid={`${testPrefix}-add-btn`}><Plus size={14} /></Button>
        </form>
      )}
    </div>
  );
}

export default function EquipmentCatalogSettings() {
  const catalog = useEquipmentCatalog();
  const types = catalog.types;
  const [busy, setBusy] = useState(false);
  const [type, setType] = useState('pump');
  const [style, setStyle] = useState('');
  const [mfr, setMfr] = useState('');
  useEffect(() => {
    if (!catalog.loading && !types.some((item) => item.value === type)) {
      setType(types[0]?.value || ''); setStyle(''); setMfr('');
    }
  }, [types, type, catalog.loading]);

  const styles = catalog.stylesFor(type);
  const mfrs = style ? catalog.manufacturersFor(type, style) : [];
  const models = style && mfr ? catalog.modelsFor(type, style, mfr) : [];

  const apply = async (req, okMsg) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await req();
      catalog.setRows(res.data.rows || []);
      if (okMsg) toast.success(okMsg);
      return res.data.rows || [];
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Catalog update failed'));
    } finally { setBusy(false); }
  };
  const add = (entry, msg) => apply(() => axios.post(`${API}/equipment-catalog`, { equipment_type: type, ...entry }), msg);
  const rename = (level, old_value, new_value) => new_value && new_value !== old_value
    && apply(() => axios.put(`${API}/equipment-catalog/rename`, { equipment_type: type, level, style, manufacturer: mfr, old_value, new_value }), 'Renamed');
  const remove = (params, msg) => apply(() => axios.delete(`${API}/equipment-catalog`, { params: { equipment_type: type, ...params } }), msg);

  const pickType = (t) => { setType(t); setStyle(''); setMfr(''); };
  const pickStyle = (s) => { setStyle(s); setMfr(''); };

  return (
    <Card className="p-6" data-testid="equipment-catalog-settings">
      <div className="flex items-start gap-3 mb-4">
        <Layers size={20} className="text-slate-400 mt-0.5" />
        <div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100" data-testid="catalog-title">Equipment Catalog</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="catalog-description">Categories, styles, manufacturers and models.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="catalog-type-tabs">
        {types.map((t) => (
          <button key={t.value} type="button" onClick={() => pickType(t.value)}
            className={`max-w-full break-all px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${type === t.value ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600'}`}
            data-testid={`catalog-type-${t.value}`}>
            {t.label} <span className="opacity-60">{catalog.stylesFor(t.value).length}</span>
          </button>
        ))}
        <Button type="button" size="sm" variant="outline" className="ml-auto" disabled={busy} onClick={() => apply(() => axios.post(`${API}/equipment-catalog/import-csm`), 'CSM pump catalogue loaded')} data-testid="catalog-import-csm-btn">
          Load CSM pump catalogue
        </Button>
      </div>
      <CatalogLoadState catalog={catalog} prefix="catalog-settings" />
      <CatalogCategoryControls selected={types.find((item) => item.value === type)} busy={busy || catalog.loading || !!catalog.error}
        onAdd={async (label) => { const rows = await apply(() => axios.post(`${API}/equipment-catalog/types`, { label }), 'Category added'); if (rows) pickType(rows.find((row) => row.type_label === label && !row.type_hidden)?.equipment_type || type); return !!rows; }}
        onRename={(label) => apply(() => axios.patch(`${API}/equipment-catalog/types/${encodeURIComponent(type)}`, { label }), 'Category renamed')}
        onRemove={() => { if (window.confirm('Remove this category from new selections? Existing assets and their labels will be preserved.')) apply(() => axios.delete(`${API}/equipment-catalog/types/${encodeURIComponent(type)}`), 'Category removed'); }} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Column title="Styles" items={styles} selected={style} busy={busy || catalog.loading || !!catalog.error} disabled={!type} onSelect={pickStyle} placeholder="New style..." testPrefix="catalog-style"
          onAdd={(v) => add({ style: v }, `Style "${v}" added`)}
          onRename={async (o, n) => { const saved = await rename('style', o, n); if (saved && style === o) setStyle(n); return saved; }}
          onDelete={async (v) => { if (await remove({ style: v }, `Style "${v}" removed`)) { if (style === v) pickStyle(''); } }} />
        <Column title="Manufacturers" items={mfrs} selected={mfr} onSelect={setMfr} busy={busy || catalog.loading || !!catalog.error} disabled={!style} placeholder="New manufacturer..." testPrefix="catalog-mfr"
          onAdd={(v) => add({ style, manufacturer: v }, `${v} added to ${style}`)}
          onRename={async (o, n) => { const saved = await rename('manufacturer', o, n); if (saved && mfr === o) setMfr(n); return saved; }}
          onDelete={async (v) => { if (await remove({ style, manufacturer: v }, `${v} removed`)) { if (mfr === v) setMfr(''); } }} />
        <Column title="Models" items={models} busy={busy || catalog.loading || !!catalog.error} disabled={!style || !mfr} placeholder="New model..." testPrefix="catalog-model"
          onAdd={(v) => add({ style, manufacturer: mfr, model: v }, `Model ${v} added`)}
          onRename={(o, n) => rename('model', o, n)}
          onDelete={(v) => remove({ style, manufacturer: mfr, model: v }, `Model ${v} removed`)} />
      </div>
    </Card>
  );
}
